import { useState, useRef, useEffect } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { initials } from "@/lib/format";
import { Upload, Loader2, ImageIcon, Plus, Trash2, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { ImplantSafetySettings } from "@/components/admin/ImplantSettings";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [profile, setProfile] = useState({
    name: user?.name || "", email: user?.email || "", clinic_name: user?.clinic_name || "",
    clinic_phone: user?.clinic_phone || "", clinic_address: user?.clinic_address || "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingClinic, setSavingClinic] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef(null);
  const [prices, setPrices] = useState([]);
  const [savingPrices, setSavingPrices] = useState(false);
  useEffect(() => { api.get("/clinic/prices").then((r) => setPrices(r.data.prices || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (user) setProfile((p) => ({
      ...p, name: user.name || "", email: user.email || "", clinic_name: user.clinic_name || "",
      clinic_phone: user.clinic_phone || "", clinic_address: user.clinic_address || "",
    }));
  }, [user]);
  const token = typeof window !== "undefined" ? localStorage.getItem("contaha_token") : "";
  const logoSrc = user?.logo_url ? (user.logo_url.startsWith("http") ? user.logo_url : `${API}/files/${user.logo_url}?auth=${token}`) : null;

  const saveProfile = async () => {
    setSavingProfile(true);
    try { await api.put("/auth/profile", { name: profile.name }); await refreshUser(); toast.success("Profile saved"); }
    catch { toast.error("Could not save profile"); }
    finally { setSavingProfile(false); }
  };
  const saveClinic = async () => {
    setSavingClinic(true);
    try {
      await api.put("/auth/profile", { clinic_name: profile.clinic_name, clinic_phone: profile.clinic_phone, clinic_address: profile.clinic_address });
      await refreshUser(); toast.success("Clinic branding saved — it will appear on all PDF reports");
    } catch { toast.error("Could not save branding"); }
    finally { setSavingClinic(false); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser(); toast.success("Logo updated");
    } catch { toast.error("Could not upload logo"); }
    finally { setUploading(false); }
  };

  const updateRow = (i, key, val) => setPrices((p) => p.map((r, x) => x === i ? { ...r, [key]: val } : r));
  const addRow = () => setPrices((p) => [...p, { procedure: "", phase: "", price: 0, visits: 1 }]);
  const removeRow = (i) => setPrices((p) => p.filter((_, x) => x !== i));
  const savePrices = async () => {
    setSavingPrices(true);
    try {
      const clean = prices.filter((r) => (r.procedure || "").trim()).map((r) => ({ procedure: r.procedure, phase: r.phase || "", price: Number(r.price) || 0, visits: Number(r.visits) || 0 }));
      await api.put("/clinic/prices", { prices: clean });
      setPrices(clean); toast.success("Pricing saved — it will drive treatment plan totals & PDFs");
    } catch { toast.error("Could not save pricing"); }
    finally { setSavingPrices(false); }
  };
  const totalCost = prices.reduce((s, r) => s + (Number(r.price) || 0), 0);
  const totalVisits = prices.reduce((s, r) => s + (Number(r.visits) || 0), 0);

  return (
    <div>
      <PageHeader title="Settings" subtitle="Manage your profile, clinic branding and preferences" breadcrumb={["Management", "Settings"]} />
      <Tabs defaultValue="profile">
        <TabsList className="mb-5 flex h-auto flex-wrap justify-start"><TabsTrigger value="profile" data-testid="settings-tab-profile">Profile</TabsTrigger><TabsTrigger value="clinic" data-testid="settings-tab-clinic">Clinic & Branding</TabsTrigger><TabsTrigger value="pricing" data-testid="settings-tab-pricing">Pricing & Duration</TabsTrigger><TabsTrigger value="preferences" data-testid="settings-tab-preferences">Preferences</TabsTrigger><TabsTrigger value="implant" data-testid="settings-tab-implant">Implant safety</TabsTrigger></TabsList>

        <TabsContent value="profile">
          <SurfaceCard className="p-6 max-w-2xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#E8F7F0] dark:bg-[#00A85A]/15 text-lg font-bold text-[#008F4D] dark:text-[#3FD98A]">{initials(user?.name)}</div>
              <div><div className="text-base font-bold text-[#101516] dark:text-white">{user?.name}</div><div className="text-sm text-[#8B9698]">{user?.role}</div></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-xs font-semibold">Full name</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="settings-name-input" className="mt-1.5" /></div>
              <div><Label className="text-xs font-semibold">Email</Label><Input value={profile.email} disabled className="mt-1.5" /></div>
            </div>
            <Button onClick={saveProfile} disabled={savingProfile} data-testid="settings-save-profile" className="mt-5 bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">{savingProfile && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Save Changes</Button>
          </SurfaceCard>
        </TabsContent>

        <TabsContent value="clinic">
          <SurfaceCard className="p-6 max-w-2xl">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-6 items-start">
              <div className="space-y-4">
                <div><Label className="text-xs font-semibold">Clinic name</Label><Input value={profile.clinic_name} onChange={(e) => setProfile({ ...profile, clinic_name: e.target.value })} data-testid="settings-clinic-name" className="mt-1.5" /></div>
                <div><Label className="text-xs font-semibold">Contact phone</Label><Input value={profile.clinic_phone} onChange={(e) => setProfile({ ...profile, clinic_phone: e.target.value })} data-testid="settings-clinic-phone" placeholder="+971 ..." className="mt-1.5" /></div>
                <div><Label className="text-xs font-semibold">Clinic address</Label><Input value={profile.clinic_address} onChange={(e) => setProfile({ ...profile, clinic_address: e.target.value })} data-testid="settings-clinic-address" placeholder="Street, City" className="mt-1.5" /></div>
                <p className="text-xs text-[#8B9698]">Your clinic name, contact and logo appear on the header and footer of every generated PDF clinical report.</p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="flex h-32 w-40 items-center justify-center overflow-hidden rounded-xl border border-[#E2E8F0] dark:border-[#223139] bg-[#F4FBF7] dark:bg-[#0E1A21]" data-testid="settings-logo-preview">
                  {logoSrc ? <img src={logoSrc} alt="Clinic logo" className="max-h-full max-w-full object-contain" /> : <div className="flex flex-col items-center text-[#8B9698]"><ImageIcon className="h-7 w-7" /><span className="mt-1 text-[10px]">No logo</span></div>}
                </div>
                <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="settings-logo-input" />
                <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} disabled={uploading} data-testid="settings-logo-upload" className="rounded-lg">{uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Upload logo</Button>
              </div>
            </div>
            <Button onClick={saveClinic} disabled={savingClinic} data-testid="settings-save-clinic" className="mt-5 bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">{savingClinic && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Save Branding</Button>
          </SurfaceCard>
        </TabsContent>

        <TabsContent value="pricing">
          <SurfaceCard className="p-6" data-testid="settings-pricing-card">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="flex items-center gap-2 text-sm font-bold text-[#101516] dark:text-white"><DollarSign className="h-4 w-4 text-[#00A85A]" />Treatment Pricing & Duration</div>
                <p className="text-xs text-[#8B9698] mt-1 max-w-lg">Configure the price and number of visits for each treatment. CONTAHA uses these to calculate the total cost and total treatment duration on every plan — the AI never invents pricing.</p>
              </div>
              <Button variant="outline" size="sm" onClick={addRow} data-testid="pricing-add-row" className="rounded-lg shrink-0"><Plus className="h-4 w-4 mr-1.5" />Add treatment</Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-[#8B9698] border-b border-[#E2E8F0] dark:border-[#223139]">
                    <th className="py-2 pr-3">Treatment / Procedure</th>
                    <th className="py-2 pr-3 w-40">Phase</th>
                    <th className="py-2 pr-3 w-28">Price ($)</th>
                    <th className="py-2 pr-3 w-24">Visits</th>
                    <th className="py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {prices.map((r, i) => (
                    <tr key={i} className="border-b border-[#E2E8F0]/60 dark:border-[#223139]/60" data-testid={`pricing-row-${i}`}>
                      <td className="py-2 pr-3"><Input value={r.procedure || ""} onChange={(e) => updateRow(i, "procedure", e.target.value)} placeholder="e.g. Implant placement" data-testid={`pricing-procedure-${i}`} className="h-9" /></td>
                      <td className="py-2 pr-3"><Input value={r.phase || ""} onChange={(e) => updateRow(i, "phase", e.target.value)} placeholder="e.g. Surgical" className="h-9" /></td>
                      <td className="py-2 pr-3"><Input type="number" min="0" value={r.price ?? ""} onChange={(e) => updateRow(i, "price", e.target.value)} data-testid={`pricing-price-${i}`} className="h-9" /></td>
                      <td className="py-2 pr-3"><Input type="number" min="0" value={r.visits ?? ""} onChange={(e) => updateRow(i, "visits", e.target.value)} data-testid={`pricing-visits-${i}`} className="h-9" /></td>
                      <td className="py-2"><Button variant="ghost" size="icon" onClick={() => removeRow(i)} data-testid={`pricing-remove-${i}`} className="h-8 w-8 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></Button></td>
                    </tr>
                  ))}
                  {prices.length === 0 && (
                    <tr><td colSpan={5} className="py-8 text-center text-sm text-[#8B9698]">No treatments configured yet. Click "Add treatment" to begin.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {prices.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-6 rounded-xl bg-[#F4FBF7] dark:bg-[#0E1A21] border border-[#E2E8F0] dark:border-[#223139] px-4 py-3">
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">Total cost</div><div className="text-lg font-extrabold text-[#00A85A]" data-testid="pricing-total-cost">${totalCost.toLocaleString()}</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">Total duration</div><div className="text-lg font-extrabold text-[#101516] dark:text-white" data-testid="pricing-total-visits">{totalVisits} visit{totalVisits === 1 ? "" : "s"}</div></div>
              </div>
            )}

            <Button onClick={savePrices} disabled={savingPrices} data-testid="settings-save-pricing" className="mt-5 bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">{savingPrices && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Save Pricing</Button>
          </SurfaceCard>
        </TabsContent>

        <TabsContent value="implant"><ImplantSafetySettings /></TabsContent>
        <TabsContent value="preferences">
          <SurfaceCard className="p-6 max-w-2xl space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-[#E2E8F0] dark:border-[#223139] p-4">
              <div><div className="text-sm font-semibold text-[#101516] dark:text-white">Dark mode</div><div className="text-xs text-[#8B9698]">Use the CONTAHA dark clinical theme</div></div>
              <Switch checked={theme === "dark"} onCheckedChange={(v) => setTheme(v ? "dark" : "light")} data-testid="settings-dark-toggle" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-[#E2E8F0] dark:border-[#223139] p-4">
              <div><div className="text-sm font-semibold text-[#101516] dark:text-white">AI review reminders</div><div className="text-xs text-[#8B9698]">Notify me when AI reports need review</div></div>
              <Switch defaultChecked data-testid="settings-ai-toggle" />
            </div>
          </SurfaceCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
